import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { LOYALTY_BASE_URL, ASSUME_ROLE_ENABLED } from "../../helpers/config";
import { AUTH } from "@/store/auth";
import { AUTH_GETTERS } from "../../store/auth/getters";
import store from "@/store";
import {
  LoyaltySchemeType,
  ISegmentOfferResponse,
  IAccountOfferResponse,
  ICampaign
} from "@/store/loyalty/types";

const DEFAULT_PAGINATION_LIMIT = 10;

// create axios client
const http: AxiosInstance = axios.create({
  baseURL: LOYALTY_BASE_URL
});

// get an access token before each request
http.interceptors.request.use(
  (config: AxiosRequestConfig): any => {
    if (ASSUME_ROLE_ENABLED) {
      const assumedRole: string | undefined =
        store.getters[AUTH.NS(AUTH_GETTERS.GET_ASSUMED_ROLE)];

      if (assumedRole) {
        config.headers["Assume-Role"] = assumedRole;
      }
    }

    return store.dispatch(AUTH.NS(AUTH.ACTIONS.ACQUIRE_LOYALTY_TOKEN)).then(
      token => {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      err => {
        console.log(err);
        return err;
      }
    );
  }
);

export default {
  getCustomers: (pagination: any): Promise<any> => {
    return http.get("/v1/customer", {
      params: {
        filter: pagination.search,
        offset: pagination.offset,
        limit: pagination.rowsPerPage || DEFAULT_PAGINATION_LIMIT,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection
      }
    });
  },
  getCustomer: (customerId: string | number): Promise<any> => {
    return http.get(`/v1/customer/${customerId}`);
  },
  getCustomerCards: (
    customerId: string | number,
    pagination: any
  ): Promise<any> => {
    return http.get(`/v1/customer/${customerId}/cards`, {
      params: {
        filter: pagination.search,
        offset: pagination.offset,
        limit: pagination.rowsPerPage || DEFAULT_PAGINATION_LIMIT,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection
      }
    });
  },
  getSegmentOffers: (): Promise<any> => {
    return http.get("/v1/segmentoffer", {
      params: {
        limit: 100
      }
    });
  },
  getSegmentOffer: (id: string | number): Promise<any> =>
    http.get(`/v1/segmentoffer/${id}`),
  putSegmentOffer: (
    id: string | number,
    segmentOfferData: ISegmentOfferResponse
  ) =>
    http.put(`/v1/segmentoffer/${id}`, {
      model: segmentOfferData
    }),
  getChannels: (): Promise<any> => http.get("/v1/channel"),
  getSegments: (): Promise<any> => http.get("/v1/segment"),
  getAccountOffers: (pagination: any): Promise<any> => {
    return http.get("/v1/accountoffer", {
      params: {
        filter: pagination.search,
        offset: pagination.offset,
        limit: pagination.rowsPerPage || DEFAULT_PAGINATION_LIMIT,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection
      }
    });
  },
  getAccountOffer: (accountnumber: string | number): Promise<any> =>
    http.get(`/v1/customer/${accountnumber}/accountoffer`),
  updateAccountOffer: (
    accountnumber: number,
    accountOffer: IAccountOfferResponse
  ): Promise<any> =>
    http.put(`/v1/customer/${accountnumber}/accountoffer`, {
      model: accountOffer
    } as any),
  deleteAccountOffer: (
    accountnumber: number ): Promise<any> =>
    http.delete(`/v1/customer/${accountnumber}/accountoffer`),
  searchCustomer: (searchTerm: string): Promise<any> =>
    http.get(`/v1/customer`, {
      params: {
        searchTerm,
        limit: 50
      }
    }),
  putCardLoyaltyAssociation: (options: any): Promise<any> => {
    const {
      accountNumber,
      loyaltyNumber,
      loyaltyScheme,
      cardNumber,
      airpointsFirstName,
      airpointsLastName
    } = options;
    return http.put(`/v1/customer/${accountNumber}/associations/card`, {
      accountNumber,
      cardNumber,
      loyaltyDetails: {
        flybuysNumber:
          loyaltyScheme === LoyaltySchemeType.FlyBuys
            ? loyaltyNumber
            : undefined,
        airPointsNumber:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? loyaltyNumber
            : undefined,
        airpointsFirstName:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? airpointsFirstName
            : undefined,
        airpointsLastName:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? airpointsLastName
            : undefined
      }
    });
  },
  putCostCentreLoyaltyAssociation: (options: any): Promise<any> => {
    const {
      accountNumber,
      loyaltyNumber,
      loyaltyScheme,
      costCentreCode,
      airpointsFirstName,
      airpointsLastName
    } = options;
    return http.put(`/v1/customer/${accountNumber}/associations/costcentre`, {
      accountNumber,
      costCentreCode,
      loyaltyDetails: {
        flybuysNumber:
          loyaltyScheme === LoyaltySchemeType.FlyBuys
            ? loyaltyNumber
            : undefined,
        airPointsNumber:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? loyaltyNumber
            : undefined,
        airpointsFirstName:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? airpointsFirstName
            : undefined,
        airpointsLastName:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? airpointsLastName
            : undefined
      }
    });
  },
  putAccountLoyaltyAssociation: (options: any): Promise<any> => {
    const {
      accountNumber,
      loyaltyNumber,
      loyaltyScheme,
      airpointsFirstName,
      airpointsLastName
    } = options;
    return http.put(`/v1/customer/${accountNumber}/associations/account`, {
      accountNumber,
      loyaltyDetails: {
        flybuysNumber:
          loyaltyScheme === LoyaltySchemeType.FlyBuys
            ? loyaltyNumber
            : undefined,
        airPointsNumber:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? loyaltyNumber
            : undefined,
        airpointsFirstName:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? airpointsFirstName
            : undefined,
        airpointsLastName:
          loyaltyScheme === LoyaltySchemeType.Airpoints
            ? airpointsLastName
            : undefined
      }
    });
  },
  getTransactions: (
    month: number,
    year: number,
    accountNumber: number | string
  ): Promise<any> => {
    return http.get("/v1/transaction", {
      params: {
        month,
        year,
        accountNumber,
        limit: 10000000 // Temp fix to get around webapi limit defaults...
      }
    });
  },
  getPoints: (
    accountNumber: string | number,
    year: number,
    month: number
  ): Promise<any> => {
    return http.get("/v1/transaction/points", {
      params: {
        accountNumber,
        year,
        month,
        limit: 10000000 // Temp fix to get around webapi limit defaults...
      }
    });
  },
  getCampaign: (id: any): Promise<any> => {
    return http.get(`/v1/campaign/${id}`);
  },
  getCampaignList: (pagination: any): Promise<any> => {
    return http.get("/v1/campaign", {
      params: {
        name: pagination.search,
        offset: pagination.offset,
        limit: pagination.rowsPerPage || DEFAULT_PAGINATION_LIMIT,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection
      }
    });
  },
  postCampaign: (campaign: ICampaign) =>
    http.post("/v1/campaign", {
      model: campaign
    }),
  putCampaign: (id: string | number, campaign: ICampaign) =>
    http.put(`/v1/campaign/${id}`, {
      model: campaign
    })
};
